


import { defineStore } from 'pinia';
import { MapUtils } from '@/utils/mapUtils';
import L from 'leaflet';
import 'leaflet-draw';
import 'proj4leaflet';
import axios from "axios";
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';
import { ElLoading } from 'element-plus';

import { addDescription, updateDescription, getCodeList, getUserJurisdiction } from "@/api/Common";

import { h, createApp } from 'vue';

import EditInfoPanel from '@/components/common/EditInfoPanel.vue';


export const useMapStore = defineStore('map', {
    state: () => ({
        map: null,
        atlasList: [],
        folderList: [],
        layers: {
            base: {},     // 基础图层（如OSM）
            overlay: {},   // 业务图层（如Marker）
            draw: {}
        },
        colors: [
            {
                index: 0,
                value: '#FF0000',
                label: '红色',
                marker: 'red'
            },
            {
                index: 1,
                value: '#FFA500',
                label: '橙色',
                marker: 'orange'
            },
            {
                index: 2,
                value: '#ffff00',
                label: '黄色',
                marker: 'yellow'
            },
            {
                index: 3,
                value: '#00ffff',
                label: '青色',
                marker: 'steelblue'
            },
            {
                index: 4,
                value: '#0000ff',
                label: '蓝色',
                marker: 'blue'
            },
            {
                index: 5,
                value: '#800080',
                label: '紫色',
                marker: 'purple'
            },
            {
                index: 6,
                value: '#000000',
                label: '黑色',
                marker: 'black'
            }
        ],
        drawLayerGrounp: null,
        areaMeasure: null,
        distanceMeasure: null,
        drawControl: null,
        drawCallback: null,
        drawObj: null,
        selectAtlas: null, // 选中的地图
        selectLayer: null,
        selectLayerCard: null,
        selectObject: null, // 选中的对象

        editObject: null,
        editObjectPopup: null,
        //用户信息
        userInfo: null,
        //用户权限
        userJurisdiction: null,

        codeList: null, // 代码列表

        isRequestCodeList: false,
        callbackList: [],
    }),
    actions: {

        async loadCodeList(callback) {

            if (this.codeList != null) {
                if (callback) {
                    try {
                        callback();
                    } catch (e) {

                    }
                }
                return;
            }
            if (this.isRequestCodeList) {
                this.callbackList.push(callback);
                return;
            }
            let that = this;
            this.isRequestCodeList = true;
            const res = await getCodeList({});
            if (res.code == 200) {
                const flatList = res.rows;
                const idToItemMap = {};
                const result = [];

                // 第一次遍历，将每个元素存入映射对象
                flatList.forEach(item => {
                    item.children = [];
                    idToItemMap[item.id] = item;
                });

                // 第二次遍历，构建父子结构
                flatList.forEach(item => {
                    if (item.pid === null || !idToItemMap[item.pid]) {
                        // 根节点
                        result.push(item);
                    } else {
                        // 子节点
                        idToItemMap[item.pid].children.push(item);
                    }
                });

                that.codeList = result;

            }
            if (this.callbackList) {

                this.callbackList.forEach(callback => {
                    if (callback && typeof callback == 'function') {
                        try {
                            callback();
                        } catch (e) {

                        }
                    }
                });
            }
        },
        async loadUserJurisdiction(callback) {
            if (this.getUserInfo() != null) {
                const res = await getUserJurisdiction({
                    userId: this.getUserInfo().userId,
                })
                if (res.code == 200) {
                    localStorage.setItem("userJurisdiction", JSON.stringify(res.rows));
                    this.userJurisdiction = res.rows;
                }
            } else {
                localStorage.setItem("userJurisdiction", JSON.stringify([]));
            }
            if (typeof callback == 'function') {
                callback();
            }
        },
        getUseJurisdiction() {
            if (this.userJurisdiction == null) {
                if (localStorage.hasOwnProperty("userJurisdiction")) {
                    this.userJurisdiction = JSON.parse(localStorage.getItem("userJurisdiction"));
                } else {
                    this.loadUserJurisdiction();
                    return [];
                }
            }
            return this.userJurisdiction;
        },
        getUserInfo() {
            if (this.userInfo == null) {
                if (localStorage.hasOwnProperty("userInfo")) {
                    this.userInfo = JSON.parse(localStorage.getItem("userInfo"));
                }
            }
            return this.userInfo;
        },
        createLayer() {
            return MapUtils.createLayer().addTo(this.map);
        },
        setSelectLayer(layer) {
            this.selectLayer = layer;
        },
        setSelectLayerCard(object) {
            this.selectLayerCard = object;
        },
        getSelectAtlas() {
            if (this.selectAtlas == null) {
                this.selectAtlas = JSON.parse(localStorage.getItem('selectAtlas'));
            }
            return this.selectAtlas;
        },
        setSelectAtlas(atlas) {
            localStorage.setItem('selectAtlas', JSON.stringify(atlas));
            this.selectAtlas = atlas;
        },
        // 初始化地图（原MapManager的initMap）
        initMap(containerId, options = { center: [31.776839, 117.236283], zoom: 13 }) {

            MapUtils.overwriteLeaflet();

            MapUtils.overwriteLeafletDraw();

            this.map = L.map(containerId, {
                center: options.center,
                zoom: options.zoom,
                crs: MapUtils.defineCrs(),
                attributionControl: false,
                zoomControl: false,
            });
            L.control.zoom({
                position: 'bottomleft', // 可选值：topleft/topright/bottomleft/bottomright
                zoomInTitle: '放大',     // 自定义按钮提示（可选）
                zoomOutTitle: '缩小'     // 自定义按钮提示（可选）
            }).addTo(this.map);

            // 判断当前没有图层组，需先添加
            if (!this.drawLayerGrounp) {
                //图层组
                this.drawLayerGrounp = new L.FeatureGroup();
                // 添加
                this.map.addLayer(this.drawLayerGrounp);
            }

            this.drawControl = MapUtils.createDrawControl(this.drawLayerGrounp);
            let that = this;
            this.map.on(L.Draw.Event.CREATED, function (e) {

                let drawLayer = e.layer;

                // 添加到图层组
                that.drawLayerGrounp.addLayer(drawLayer);

                if (that.drawCallback) {
                    that.drawCallback(drawLayer, () => {
                        that.drawLayerGrounp.removeLayer(drawLayer);
                    }); // 调用回调函数
                }
            });

        },
        //创建编辑弹窗
        createEditPopup(object, item, saveObject) {

            // 移除对象的点击事件
            object.off('click');
            let that = this;
            // 创建一个 DOM 元素用于挂载 Vue 组件
            const container = document.createElement('div');
            // 设置 popup 容器的宽度
            container.style.width = '300px';
            const vnode = h(EditInfoPanel, {
                item: item,
                onSave: (newItem) => {
                    saveObject(newItem);
                }
            });
            // 使用 createApp 创建应用实例并挂载
            const app = createApp({
                render: () => vnode
            });
            app.mount(container);

            // 先移除已绑定的 popup
            if (object.getPopup()) {
                this.editObjectPopup = object.getPopup();
                object.unbindPopup();
            }
            // 创建 popup 并绑定到对象上
            let popup = object.bindPopup(container);
            popup.openPopup();
            // 监听 popup 关闭事件
            popup.on('popupclose', function () {
                // console.log("popupclose");
                // 卸载 Vue 组件

            });
            // 阻止对象点击事件冒泡到地图，避免触发默认关闭 popup 行为
            object.on('click', function (e) {
                // console.log("click");
                popup.openPopup();
            });
            // 监听编辑过程中坐标变化
            object.on('dragend', function () {
                popup.openPopup();
            });

            // // 获取对象的中心位置，不同类型对象获取方式不同
            // let latLng;
            // let offsetY = 0;
            // if (object instanceof L.Marker) {
            //     latLng = object.getLatLng();
            //     offsetY = -66;
            // } else if (object instanceof L.Polygon || object instanceof L.Polyline) {
            //     // 计算多边形或折线的中心
            //     latLng = object.getBounds().getCenter();
            // } else if (object instanceof L.Circle) {
            //     latLng = object.getLatLng();
            // }

            // if (latLng) {
            //     // 创建 popup 并直接在地图上显示
            //     // let popup = L.popup({
            //     //     // 通过 offset 选项设置偏移，第二个参数为负表示向上偏移，可按需调整数值
            //     //     offset: L.point(0, offsetY)
            //     // })
            //     //     .setLatLng(latLng)
            //     //     .setContent(container)
            //     //     .openOn(this.map);

            //     // 创建 popup 并绑定到对象上

            // }
        },
        endEditObject() {
            if (this.editObject) {
                this.editObject.bindPopup(this.editObjectPopup);
                this.editObject.editing.disable();
                this.editObject = null;
                this.editObjectPopup = null;
            }
        },
        lonlatToTile(lon, lat, zoom = 9) {
            try {
                const n = Math.pow(2, zoom);
                const x = Math.floor((lon + 180) / 360 * n);
                const latRad = lat * Math.PI / 180;
                const y = Math.floor((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n);
                return { x, y };
            } catch (e) { }
            return { x: 0, y: 0 };
        },

        //根据坐标获取行政区划代码
        async getAdCodeByLatLng(lng, lat, callback) {
            let that = this;

            let tile = this.lonlatToTile(lng, lat, 9);


            let url = MAP_URL + "/9/" + tile.x + "/" + tile.y + ".geojson";
            let data = "100000";
            let city = "中国";
            try {
                // 使用 await 等待请求完成
                const response = await axios.get(url);
                const geojson = response.data;
                const pointToCheck = point([lng, lat]);
                // console.log(geojson);
                if (geojson.type === 'FeatureCollection') {
                    for (const feature of geojson.features) {
                        if (feature.geometry && feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
                            if (booleanPointInPolygon(pointToCheck, feature)) {
                                // 假设 GeoJSON 中的行政区划代码字段为 'adcode'，根据实际情况修改
                                data = feature.properties['code'] || "100000";
                                city = feature.properties['地名'] || "中国";
                            }
                        }
                    }
                }


            } catch (error) {
                console.log(error);
                data = "100000";
                city = "中国";
            }
            if (typeof callback == 'function') {
                callback(data, city);
            }

        },
        enableEditObject(object, item, layerCard) {
            let that = this;


            if (this.editObject) {
                this.editObject.editing.disable();
                this.drawLayerGrounp.removeLayer(this.editObject);
                this.editObject = null;
            }
            object.editing.enable();

            this.editObject = object;

            this.createEditPopup(
                object, item,
                (newItem) => {
                    let params = {
                        // "layerId": layerCard.item.id,
                        "title": newItem.title,
                        "description": newItem.description,
                        "type": item.type,
                        "userId": this.getUserInfo().userId,
                    };
                    let latitude;
                    let longitude;
                    let latLngs;
                    let locations;

                    switch (item.type) {
                        case '0':
                            let latLng;
                            // 根据不同图层类型获取经纬度
                            if (object instanceof L.Marker) {
                                latLng = object.getLatLng();
                            } else if (object instanceof L.Circle || object instanceof L.CircleMarker) {
                                latLng = object.getLatLng();
                            } else if (object instanceof L.Polygon || object instanceof L.Polyline) {
                                // 多边形和折线取第一个点作为代表，可按需调整
                                latLng = object.getLatLngs()[0];
                            }
                            if (latLng) {
                                latitude = latLng.lat;
                                longitude = latLng.lng;
                            }
                            params.longitude = longitude;
                            params.latitude = latitude;
                            params.locations = [{
                                "longitude": longitude,
                                "latitude": latitude,
                            }];
                            break;
                        case '1':
                            latLngs = object.getLatLngs();
                            latitude = latLngs[0].lat;
                            longitude = latLngs[0].lng;

                            locations = latLngs.map(latLng => ({
                                longitude: latLng.lng,
                                latitude: latLng.lat
                            }));
                            params.longitude = longitude;
                            params.latitude = latitude;
                            params.locations = locations;
                            break;
                        case '2':
                            latLngs = object.getLatLngs();
                            // 处理多边形嵌套的情况
                            if (latLngs[0] && latLngs[0].length) {
                                latLngs = latLngs[0];
                            }
                            latitude = latLngs[0].lat;
                            longitude = latLngs[0].lng;
                            locations = latLngs.map(latLng => ({
                                longitude: latLng.lng,
                                latitude: latLng.lat
                            }));
                            params.longitude = longitude;
                            params.latitude = latitude;
                            params.locations = locations;
                            break;
                    }
                    that.getAdCodeByLatLng(params.longitude, params.latitude, (data, city) => {
                        params.code = data;
                        const loading = ElLoading.service({
                            lock: true,
                            text: 'Loading',
                            background: 'rgba(0, 0, 0, 0.7)',
                        });
                        if (item.id) {
                            params.id = item.id;
                            updateDescription(params).then(res => {

                                if (res.code == 200) {
                                    that.drawLayerGrounp.removeLayer(object);
                                    layerCard.addDescription(params, object, city);
                                }
                                loading.close();
                                alert(res.msg);
                            })
                        } else {
                            addDescription(params).then(res => {

                                if (res.code == 200) {
                                    that.drawLayerGrounp.removeLayer(object);
                                    layerCard.addDescription(params, object, city);
                                }
                                loading.close();
                                alert(res.msg);
                            })
                        }
                        object.editing.disable();
                        that.editObject = null;

                    });

                }
            );



        },
        saveObject() {


        },

        startDraw(type, callback) {
            // 先取消
            if (this.drawObj) {
                this.drawObj.disable();
            }
            switch (type) {
                case 'marker': {// 点
                    this.drawObj = new L.Draw.Marker(this.map, this.drawControl.options.draw.marker);
                    break;
                }
                case 'circle': {// 圆
                    this.drawObj = new L.Draw.Circle(this.map, this.drawControl.options.draw.circle);
                    break;
                }
                case 'polyline': {// 线
                    this.drawObj = new L.Draw.Polyline(this.map, this.drawControl.options.draw.polyline);
                    break;
                }
                case 'rectangle': {// 矩形
                    this.drawObj = new L.Draw.Rectangle(this.map, this.drawControl.options.draw.rectangle);
                    break;
                }
                case 'polygon': {// 多边形
                    this.drawObj = new L.Draw.Polygon(this.map, this.drawControl.options.draw.polygon);
                    break;
                }
            }
            // 启动 
            this.drawObj.enable();
            this.drawCallback = callback;
        },

        // 添加基础图层
        addBaseLayer(key, layer) {
            this.layers.base[key] = layer.addTo(this.map);
        },
        // 添加业务图层
        addOverlayLayer(key, layer) {
            this.layers.overlay[key] = layer.addTo(this.map);
        },
        measureArea() {
            if (this.areaMeasure == null) {
                this.areaMeasure = MapUtils.createAreaMeasure({ map: this.map });
            }
            this.areaMeasure.init();
        },
        measureDistance() {
            if (this.distanceMeasure == null) {
                this.distanceMeasure = MapUtils.createDistanceMeasure({ map: this.map });
            }
            this.distanceMeasure.init();
        },
        addControl(control) {
            this.map.addControl(control);
        }


    }
});